import { useEffect, useRef, useState } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";
import { Loader2, Eye, Users, RefreshCw, Download, ExternalLink } from "lucide-react";
import { http } from "../lib/api";

// PDF.js worker — servi depuis un CDN aligné sur la version installée pour éviter les problèmes
// de MIME type .mjs ou de bundling côté dev (observé : "Failed to fetch dynamically imported module").
pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

/**
 * Aperçu PDF live d'un modèle de document, rendu via PDF.js (canvas) pour fonctionner
 * dans tous les navigateurs. Debounce 500ms sur modifications.
 * Affiche TOUTES les pages du PDF en scroll vertical (pas de pagination).
 */
export default function DocPreviewPane({ body, title, logoSizeCm, footerExtraLine = "",
    footerExtraFontSize = 7, footerExtraColor = "#475569",
    footerExtraBold = false, footerExtraItalic = true,
    kind = "generic", debounceMs = 500, defaultAgentId = "" }) {
  const [agents, setAgents] = useState([]);
  const [agentId, setAgentId] = useState(defaultAgentId || "");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [pdfData, setPdfData] = useState(null); // Uint8Array
  const [numPages, setNumPages] = useState(0);
  const containerRef = useRef(null);
  const [width, setWidth] = useState(0);
  const lastReqRef = useRef(0);
  const [downloadUrl, setDownloadUrl] = useState("");

  useEffect(() => {
    http.get("/agents").then(r => setAgents(r.data || [])).catch(() => {});
  }, []);

  // Mesurer la largeur du conteneur pour un rendu adaptatif
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const update = () => setWidth(el.clientWidth - 16);
    update();
    const obs = new ResizeObserver(update);
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  // Nettoyer l'URL de téléchargement précédente
  useEffect(() => () => { if (downloadUrl) URL.revokeObjectURL(downloadUrl); }, [downloadUrl]);

  useEffect(() => {
    const timer = setTimeout(async () => {
      if (!body || !body.trim()) { setPdfData(null); setNumPages(0); return; }
      const reqId = ++lastReqRef.current;
      setLoading(true); setErr("");
      try {
        const resp = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/doc-templates/preview-pdf`, {
          method: "POST", credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            title: title || "Aperçu",
            body,
            logo_size_cm: logoSizeCm || 5,
            agent_id: agentId || null,
            footer_extra_line: footerExtraLine || "",
            footer_extra_font_size: footerExtraFontSize || 7,
            footer_extra_color: footerExtraColor || "#475569",
            footer_extra_bold: !!footerExtraBold,
            footer_extra_italic: footerExtraItalic !== false,
            kind: kind || "generic",
          }),
        });
        if (reqId !== lastReqRef.current) return;
        if (!resp.ok) {
          const txt = await resp.text();
          throw new Error(txt || `HTTP ${resp.status}`);
        }
        const buf = await resp.arrayBuffer();
        const bytes = new Uint8Array(buf);
        setPdfData({ data: bytes });
        // Mettre à jour l'URL de téléchargement (optionnel)
        const blob = new Blob([bytes], { type: "application/pdf" });
        const url = URL.createObjectURL(blob);
        setDownloadUrl((old) => { if (old) URL.revokeObjectURL(old); return url; });
      } catch (e) {
        setErr(e?.message || String(e));
      } finally {
        if (reqId === lastReqRef.current) setLoading(false);
      }
    }, debounceMs);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [body, title, logoSizeCm, agentId, footerExtraLine, footerExtraFontSize, footerExtraColor, footerExtraBold, footerExtraItalic, kind]);

  const onLoadSuccess = ({ numPages }) => {
    setNumPages(numPages);
  };

  const forceRefresh = () => {
    lastReqRef.current++;
    // force déclenchement de l'effet via un setter sans valeur ; on remet la même valeur
    setPdfData((d) => (d ? { ...d } : d));
  };

  return (
    <div className="flex flex-col h-full bg-slate-50 border border-slate-200 rounded-md overflow-hidden" data-testid="live-preview">
      <div className="flex items-center gap-2 px-3 py-2 border-b border-slate-200 bg-white">
        <Eye className="h-4 w-4 text-sky-600" />
        <div className="text-[10px] font-bold uppercase tracking-widest text-slate-600 flex-1">Aperçu PDF en direct</div>
        {loading && <Loader2 className="h-3.5 w-3.5 animate-spin text-sky-600" />}
      </div>
      <div className="flex items-center gap-2 px-3 py-2 border-b border-slate-200 bg-slate-50 text-xs">
        <Users className="h-3.5 w-3.5 text-slate-500" />
        <span className="text-slate-600 whitespace-nowrap">Avec :</span>
        <select data-testid="preview-agent-select" value={agentId} onChange={(e)=>setAgentId(e.target.value)}
                className="flex-1 min-w-0 border border-slate-300 rounded px-2 py-1 text-xs bg-white">
          <option value="">Agent-démo (Jean DUPONT)</option>
          {agents.map(a => (
            <option key={a.id} value={a.id}>
              {a.last_name} {a.first_name}
            </option>
          ))}
        </select>
        <button onClick={forceRefresh} className="p-1 hover:bg-slate-200 rounded" title="Rafraîchir">
          <RefreshCw className={`h-3.5 w-3.5 text-slate-600 ${loading ? "animate-spin" : ""}`} />
        </button>
        {downloadUrl && (
          <>
            <a href={downloadUrl} download="apercu.pdf" className="p-1 hover:bg-slate-200 rounded" title="Télécharger l'aperçu">
              <Download className="h-3.5 w-3.5 text-slate-600" />
            </a>
            <a href={downloadUrl} target="_blank" rel="noopener noreferrer" className="p-1 hover:bg-slate-200 rounded" title="Ouvrir dans un onglet">
              <ExternalLink className="h-3.5 w-3.5 text-slate-600" />
            </a>
          </>
        )}
      </div>
      {err && <div className="px-3 py-2 text-xs text-rose-700 bg-rose-50 border-b border-rose-200">{err}</div>}
      <div ref={containerRef} className="flex-1 relative bg-slate-200 overflow-auto">
        {!pdfData ? (
          <div className="absolute inset-0 grid place-items-center text-slate-400 text-sm text-center p-6">
            {body?.trim() ? "Préparation de l'aperçu…" : "Commencez à rédiger pour voir l'aperçu PDF ici."}
          </div>
        ) : (
          <div className="flex flex-col items-center gap-3 py-3">
            <Document
              file={pdfData}
              onLoadSuccess={onLoadSuccess}
              onLoadError={(e)=>setErr(e?.message || "Erreur lecture PDF")}
              loading={<div className="text-slate-500 text-sm py-10"><Loader2 className="h-4 w-4 animate-spin inline mr-2"/>Chargement…</div>}
            >
              {width > 0 && numPages > 0 && Array.from({ length: numPages }, (_, i) => (
                <div key={`page-${i+1}`} className="shadow-lg bg-white mb-3 last:mb-0" data-testid={`preview-page-${i+1}`}>
                  <Page
                    pageNumber={i + 1}
                    width={Math.min(width, 820)}
                    renderAnnotationLayer={false}
                    renderTextLayer={false}
                  />
                </div>
              ))}
            </Document>
          </div>
        )}
      </div>
      {numPages > 0 && (
        <div className="px-3 py-1.5 border-t border-slate-200 bg-white text-[11px] text-slate-500 text-center font-mono">
          {numPages} page{numPages > 1 ? "s" : ""} · faire défiler pour tout voir
        </div>
      )}
    </div>
  );
}
